import { t } from "../../i18n";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Image,
  Search,
  Star,
  Wrench,
  X,
} from "lucide-react";
import {
  filterModels,
  modelSupportsVision,
  type Model,
  type ModelPreferences,
} from "@lumen/shared";
import { api } from "../../api";
import { ModelTooltipRow } from "../models/ModelTooltipRow";

type Filter = "vision" | "imageGeneration" | "tools" | string;
type CapabilityFilter = "vision" | "imageGeneration" | "tools";
const capabilityFilters = ["vision", "imageGeneration", "tools"] as const;

const capabilityLabels: Record<string, string> = {
  vision: "Vision",
  imageGeneration: "Image generation",
  tools: "Tools",
};

function displayMetadata(value: unknown): string {
  if (value == null) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  if (Array.isArray(value))
    return value.map(displayMetadata).filter(Boolean).join(", ");
  return Object.entries(value as Record<string, unknown>)
    .map(([key, entry]) => `${key}: ${displayMetadata(entry)}`)
    .join(", ");
}

function modelDetails(model: Model) {
  const details: string[] = [];
  if (model.contextLength)
    details.push(`Context: ${model.contextLength.toLocaleString()} tokens`);
  if (model.pricing && Object.keys(model.pricing).length)
    details.push(`Pricing: ${displayMetadata(model.pricing)}`);
  if (model.architecture && Object.keys(model.architecture).length)
    details.push(`Architecture: ${displayMetadata(model.architecture)}`);
  const supportedParameters = Array.isArray(model.supportedParameters)
    ? model.supportedParameters
    : [];
  if (supportedParameters.length)
    details.push(`Parameters: ${supportedParameters.join(", ")}`);
  return details;
}

export const ModelSearchPicker = memo(function ModelSearchPicker({
  models,
  value,
  onChange,
  disabled,
  emptyLabel,
  preferences,
  onPreferencesChange,
  hideUniformCapabilityFilters = false,
  defaultModelId,
  onSetDefault,
  setDefaultLabel,
}: {
  models: Model[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  emptyLabel?: string;
  preferences?: ModelPreferences;
  onPreferencesChange?: (preferences: ModelPreferences) => void;
  hideUniformCapabilityFilters?: boolean;
  /** Effective default to highlight/save against (overrides preferences-derived default). */
  defaultModelId?: string | null;
  /** Custom "set default" handler (e.g. per-mode defaults) replacing the global one. */
  onSetDefault?: (id: string) => void;
  setDefaultLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [preferenceBusy, setPreferenceBusy] = useState(false);
  const [preferenceError, setPreferenceError] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const selected = models.find((model) => model.id === value);
  const effectiveDefaultModelId =
    defaultModelId !== undefined
      ? defaultModelId
      : (preferences?.defaultModelId ?? preferences?.adminDefaultModelId);
  const tags = useMemo(
    () =>
      [
        ...new Set(
          models.flatMap((model) =>
            Array.isArray(model.tags) ? model.tags : [],
          ),
        ),
      ].sort(),
    [models],
  );
  const providers = useMemo(
    () =>
      [
        ...new Set(
          models
            .map((model) => model.providerName)
            .filter((name): name is string => Boolean(name)),
        ),
      ].sort(),
    [models],
  );
  const visibleCapabilityFilters = useMemo(() => {
    if (!hideUniformCapabilityFilters) return capabilityFilters;
    return capabilityFilters.filter((name) => {
      const supported = models.filter((model) =>
        name === "tools"
          ? model.capabilities.tools && model.toolsEnabled
          : model.capabilities[name],
      ).length;
      return supported > 0 && supported < models.length;
    });
  }, [hideUniformCapabilityFilters, models]);
  const filtered = useMemo(
    () =>
      filterModels(models, {
        query,
        provider,
        capability:
          filter && capabilityFilters.includes(filter as CapabilityFilter)
            ? filter
            : undefined,
        tag:
          filter && !capabilityFilters.includes(filter as CapabilityFilter)
            ? filter
            : undefined,
      }),
    [filter, models, provider, query],
  );
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  useEffect(() => {
    if (
      filter &&
      capabilityFilters.includes(filter as CapabilityFilter) &&
      !visibleCapabilityFilters.includes(filter as CapabilityFilter)
    )
      setFilter(null);
  }, [filter, visibleCapabilityFilters]);

  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };
  const updatePreferences = async (
    patch: Partial<
      Pick<ModelPreferences, "defaultModelId" | "favoriteModelIds">
    >,
  ) => {
    if (!preferences || !onPreferencesChange || preferenceBusy) return;
    const previous = preferences;
    const optimistic = { ...previous, ...patch };
    setPreferenceBusy(true);
    setPreferenceError("");
    onPreferencesChange(optimistic);
    window.dispatchEvent(
      new CustomEvent("lumen:model-preferences", { detail: optimistic }),
    );
    try {
      const saved = await api<ModelPreferences>("/me/model-preferences", {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      onPreferencesChange(saved);
      window.dispatchEvent(
        new CustomEvent("lumen:model-preferences", { detail: saved }),
      );
    } catch (reason) {
      onPreferencesChange(previous);
      window.dispatchEvent(
        new CustomEvent("lumen:model-preferences", { detail: previous }),
      );
      setPreferenceError(
        reason instanceof Error
          ? reason.message
          : "Preference could not be saved",
      );
    } finally {
      setPreferenceBusy(false);
    }
  };
  return (
    <div ref={root} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="button flex max-w-[min(22rem,65vw)] items-center gap-2 px-2.5 py-1 text-left"
      >
        <span className="text-lg" aria-hidden>
          {selected?.icon || "✦"}
        </span>
        <span className="truncate text-sm font-semibold">
          {selected?.name ?? emptyLabel ?? "Choose a model"}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {preferences && value && value !== effectiveDefaultModelId && (
        <button
          disabled={preferenceBusy}
          type="button"
          className="ml-10 block cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-4 font-medium text-lumen-700 transition-colors hover:text-lumen-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-lumen-300 dark:hover:text-lumen-100"
          onClick={() =>
            onSetDefault
              ? onSetDefault(value)
              : void updatePreferences({ defaultModelId: value })
          }
        >
          {setDefaultLabel ?? t("Set as default")}
        </button>
      )}
      {preferenceError && (
        <span
          role="alert"
          className="absolute top-full left-10 z-50 mt-1 rounded-lg bg-red-50 px-2 py-1 text-[11px] text-red-700 shadow dark:bg-red-950 dark:text-red-200"
        >
          {preferenceError}
        </span>
      )}
      {open && (
        <div className="mobile-fullscreen fixed inset-0 z-[100] flex h-[100dvh] flex-col overflow-hidden border-stone-200 bg-white shadow-2xl sm:absolute sm:inset-auto sm:top-full sm:left-0 sm:z-40 sm:mt-2 sm:block sm:h-auto sm:w-[min(28rem,calc(100vw-2rem))] sm:rounded-2xl sm:border dark:border-white/10 dark:bg-stone-900">
          <div className="flex h-14 shrink-0 items-center border-b border-stone-200 px-4 sm:hidden dark:border-white/10">
            <strong>{t("Choose a model")}</strong>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="button ml-auto p-2"
              aria-label={t("Close model selection")}
            >
              <X size={19} />
            </button>
          </div>
          <div className="border-b border-stone-200 p-3 dark:border-white/10">
            <label className="flex items-center gap-2 rounded-xl bg-stone-100 px-3 dark:bg-white/5">
              <Search size={15} className="text-stone-400" />
              <span className="sr-only">{t("Search models")}</span>
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t(
                  "Search models, providers, tags, or capabilities",
                )}
                className="model-picker-search min-w-0 flex-1 bg-transparent py-2.5 text-sm"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t("Clear search")}
                >
                  <X size={14} />
                </button>
              )}
            </label>
            <div
              className="mt-2 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto"
              aria-label={t("Filter models")}
            >
              {providers.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setProvider(provider === name ? null : name)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${provider === name ? "bg-lumen-600 text-white" : "bg-stone-100 text-stone-600 dark:bg-white/5 dark:text-stone-300"}`}
                >
                  {name}
                </button>
              ))}
              {visibleCapabilityFilters.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setFilter(filter === name ? null : name)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${filter === name ? "bg-lumen-600 text-white" : "bg-stone-100 text-stone-600 dark:bg-white/5 dark:text-stone-300"}`}
                >
                  {capabilityLabels[name]}
                </button>
              ))}
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setFilter(filter === tag ? null : tag)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${filter === tag ? "bg-lumen-600 text-white" : "bg-stone-100 text-stone-600 dark:bg-white/5 dark:text-stone-300"}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div
            role="listbox"
            aria-label={t("Models")}
            className="min-h-0 flex-1 overflow-y-auto p-2 sm:max-h-80 sm:flex-none"
          >
            {emptyLabel && (
              <button
                role="option"
                aria-selected={!value}
                type="button"
                onClick={() => choose("")}
                className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left hover:bg-stone-100 dark:hover:bg-white/5"
              >
                <span className="grid size-8 place-items-center rounded-lg bg-stone-100 dark:bg-white/5">
                  —
                </span>
                <span className="flex-1 text-sm font-semibold">
                  {emptyLabel}
                </span>
                {!value && <Check size={16} className="text-lumen-600" />}
              </button>
            )}
            {filtered.map((model) => {
              const details = modelDetails(model);
              return (
                <ModelTooltipRow key={model.id} model={model} details={details}>
                  <button
                    role="option"
                    data-model-id={model.id}
                    aria-selected={model.id === value}
                    onClick={() => choose(model.id)}
                    className="flex min-w-0 flex-1 items-start gap-3 p-2.5 text-left"
                  >
                    <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-stone-100 text-base dark:bg-white/5">
                      {model.icon || "✦"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">
                        {model.name}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-[11px] text-stone-400">
                        {modelSupportsVision(model) && <Image size={11} />}{" "}
                        {model.capabilities.tools && model.toolsEnabled && (
                          <Wrench size={11} />
                        )}
                        <span className="truncate">
                          {model.description || model.remoteId}
                        </span>
                      </span>
                      {model.tags.filter((tag) => tag !== filter).length >
                        0 && (
                        <span className="mt-1 flex flex-wrap gap-1">
                          {model.tags
                            .filter((tag) => tag !== filter)
                            .map((tag) => (
                              <span
                                key={tag}
                                title={tag}
                                className="max-w-32 truncate rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-500 dark:bg-white/5 dark:text-stone-400"
                              >
                                {tag}
                              </span>
                            ))}
                        </span>
                      )}
                    </span>
                    <span className="mt-1.5 flex shrink-0 items-center gap-1">
                      {effectiveDefaultModelId === model.id && (
                        <span className="rounded bg-lumen-50 px-1.5 py-0.5 text-[10px] font-bold text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
                          {t("Default")}
                        </span>
                      )}
                      {model.id === value && (
                        <Check size={16} className="text-lumen-600" />
                      )}
                    </span>
                  </button>
                  {preferences && (
                    <button
                      type="button"
                      className={`m-2 self-center rounded-lg p-1.5 ${preferences.favoriteModelIds.includes(model.id) ? "text-amber-500" : "text-stone-300 hover:text-amber-500"}`}
                      aria-label={`${preferences.favoriteModelIds.includes(model.id) ? t("Remove") : t("Add")} ${model.name} ${preferences.favoriteModelIds.includes(model.id) ? "from" : "to"} favorites`}
                      title={t("Toggle favorite shortcut")}
                      onClick={() =>
                        void updatePreferences({
                          favoriteModelIds:
                            preferences.favoriteModelIds.includes(model.id)
                              ? preferences.favoriteModelIds.filter(
                                  (id) => id !== model.id,
                                )
                              : [...preferences.favoriteModelIds, model.id],
                        })
                      }
                    >
                      <Star
                        size={16}
                        fill={
                          preferences.favoriteModelIds.includes(model.id)
                            ? "currentColor"
                            : "none"
                        }
                      />
                    </button>
                  )}
                </ModelTooltipRow>
              );
            })}
            {!filtered.length && (
              <div className="px-4 py-8 text-center text-sm text-stone-500">
                {t("No models match your search.")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
